import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AiMarketingService } from '@admin-core/services/ai-marketing.service';
import { MetaChannelStatus, MetaIntegrations } from '@admin-core/models/ai-marketing.model';
import { LanguageService } from '@admin-core/i18n/language.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { ConfirmDialogService } from '@admin-shared/components/confirm-dialog/confirm-dialog.service';
import { AiMarketingConnectComponent } from './ai-marketing-connect.component';

@Component({
  selector: 'app-ai-marketing-hub',
  standalone: true,
  imports: [RouterLink, TranslatePipe, AiMarketingConnectComponent],
  templateUrl: './ai-marketing-hub.component.html',
  styleUrl: './ai-marketing-admin.scss'
})
export class AiMarketingHubComponent implements OnInit {
  private readonly api = inject(AiMarketingService);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LanguageService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly integrations = signal<MetaIntegrations | null>(null);
  readonly connectModalOpen = signal(false);
  readonly connectReconnect = signal(false);
  readonly connectResume = signal(false);

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    if (params.get('meta_error') || params.get('meta_connected')) {
      this.connectModalOpen.set(true);
    }
    if (params.get('meta_connected')) {
      this.connectResume.set(true);
    }

    this.loadIntegrations();
  }

  onConnectAuthorized(): void {
    this.connectResume.set(false);
    this.reload();
  }

  openConnectModal(options?: { reconnect?: boolean; resume?: boolean }): void {
    this.connectReconnect.set(options?.reconnect ?? false);
    this.connectResume.set(options?.resume ?? false);
    this.connectModalOpen.set(true);
  }

  closeConnectModal(): void {
    this.connectModalOpen.set(false);
    this.connectReconnect.set(false);
    this.connectResume.set(false);
  }

  onConnectCompleted(): void {
    this.connectModalOpen.set(false);
    this.reload();
  }

  reload(): void {
    this.loadIntegrations();
  }

  isConnected(channel: MetaChannelStatus | null | undefined): boolean {
    return channel?.status === 'Connected';
  }

  hasAuthorization(): boolean {
    return (this.integrations()?.authorizations?.length ?? 0) > 0;
  }

  hasAnyConnection(): boolean {
    const i = this.integrations();
    return this.isConnected(i?.facebookPage) || this.isConnected(i?.instagram);
  }

  needsAccountSelection(): boolean {
    return this.hasAuthorization() && !this.hasAnyConnection();
  }

  disconnectMeta(): void {
    void this.confirmDialog
      .confirm({
        title: this.i18n.t('aiMarketing.disconnectTitle'),
        message: this.i18n.t('aiMarketing.disconnectMessage')
      })
      .then((ok) => {
        if (!ok) return;
        this.api.disconnect().subscribe({
          next: () => this.reload(),
          error: (err) => this.fail(err)
        });
      });
  }

  private loadIntegrations(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.integrations.set(null);

    this.api.getIntegrations().subscribe({
      next: (i) => {
        this.integrations.set(i);
        this.loading.set(false);
      },
      error: (err) => {
        this.integrations.set(null);
        this.loading.set(false);
        this.loadError.set(err?.error?.error ?? this.i18n.t('aiMarketing.loadError'));
      }
    });
  }

  private fail(err: { error?: { error?: string } }): void {
    void this.confirmDialog.alert({
      title: this.i18n.t('common.errorTitle'),
      message: err?.error?.error ?? this.i18n.t('aiMarketing.loadError')
    });
  }
}
